/**
 * Canonical trail amenity definitions. The key is what we store in
 * trail_attributes (as `amenity.<key>`); the label is what users see;
 * the icon is an SVG path mirroring the corresponding SF Symbol's
 * geometric style so the web and iOS look like they came from the same
 * design family. Single-color, 24×24, stroke-based.
 *
 * Storage convention: the attribute row's existence == "amenity is
 * available." Unchecking deletes the row instead of writing false.
 */

import type { ReactNode } from "react";

export interface Amenity {
  key: string;
  label: string;
  /** Matching SF Symbol name on iOS so both surfaces feel consistent. */
  sfSymbol: string;
  icon: ReactNode;
}

const STROKE = "currentColor";
const stroke = {
  fill: "none",
  stroke: STROKE,
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const AMENITIES: Amenity[] = [
  {
    key: "maps",
    label: "Maps",
    sfSymbol: "map",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2V6z" />
        <path d="M9 4v16M15 6v16" />
      </svg>
    ),
  },
  {
    key: "bathrooms",
    label: "Bathrooms",
    sfSymbol: "toilet",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M5 4h14v6a4 4 0 01-4 4H9a4 4 0 01-4-4V4z" />
        <path d="M9 14v4a2 2 0 002 2h2a2 2 0 002-2v-4" />
        <path d="M8 4h8" />
      </svg>
    ),
  },
  {
    key: "water_fountain",
    label: "Water Fountain",
    sfSymbol: "drop.fill",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M12 3c0 0-6 6.5-6 11a6 6 0 1012 0c0-4.5-6-11-6-11z" />
        <path d="M9 14a3 3 0 003 3" />
      </svg>
    ),
  },
  {
    key: "bike_tools",
    label: "Bike Tools",
    sfSymbol: "wrench.adjustable",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M14.5 4a4.5 4.5 0 015.5 5.5L9 21l-5-1.5L4 14 14.5 3.5z" />
        <path d="M14 5l5 5" />
      </svg>
    ),
  },
  {
    key: "parking",
    label: "Parking",
    sfSymbol: "parkingsign",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <path d="M9 8v8M9 8h4a3 3 0 010 6H9" />
      </svg>
    ),
  },
  {
    key: "paid_parking",
    label: "Paid Parking",
    sfSymbol: "dollarsign.circle",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="12" cy="12" r="9" />
        <path d="M14.5 9.5c0-1.4-1.1-2-2.5-2s-2.5.6-2.5 2c0 2.8 5 1.6 5 4.5 0 1.4-1.1 2-2.5 2s-2.5-.6-2.5-2" />
        <path d="M12 5.5v13" />
      </svg>
    ),
  },
  {
    key: "skills_yard",
    label: "Skills Yard",
    sfSymbol: "figure.outdoor.cycle",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="6" cy="17" r="3" />
        <circle cx="18" cy="17" r="3" />
        <path d="M6 17l4-7h5l3 7" />
        <path d="M10 10l3-4 2 1" />
        <circle cx="15" cy="5" r="1.2" />
      </svg>
    ),
  },
  {
    key: "freeride",
    label: "Freeride",
    sfSymbol: "mountain.2.fill",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M2 20l6-10 4 6 3-4 7 8H2z" />
        <path d="M8 10l-1-3 2-2 2 4" />
      </svg>
    ),
  },
  {
    key: "pavilion",
    label: "Pavilion",
    sfSymbol: "building.columns.fill",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M2 9l10-6 10 6" />
        <path d="M4 9v10M20 9v10M9 9v10M15 9v10" />
        <path d="M2 21h20" />
      </svg>
    ),
  },
  {
    key: "picnic_tables",
    label: "Picnic Tables",
    // Matches the SF Symbol used on iOS — table-with-legs silhouette
    // rather than the figure-eating one we had originally.
    sfSymbol: "table.furniture.fill",
    icon: (
      <svg viewBox="0 0 24 24" {...stroke}>
        {/* Table top */}
        <path d="M3 10h18" />
        {/* Legs angled out */}
        <path d="M6 10l-2 10M18 10l2 10" />
        {/* Bench line */}
        <path d="M5 16h14" />
      </svg>
    ),
  },
];

export const AMENITY_KEYS = AMENITIES.map((a) => a.key);

export function amenityKeyToAttribute(key: string): string {
  return `amenity.${key}`;
}

export function attributeKeyToAmenityKey(key: string): string | null {
  return key.startsWith("amenity.") ? key.slice("amenity.".length) : null;
}

/**
 * Warm section header — replaces AirBnB's "What this place offers" with
 * something that covers parking-lot and trailhead-adjacent perks too,
 * not just things literally on the trail.
 */
export const AMENITIES_HEADING = "Here when you arrive";
